import { useEffect, useState, useRef } from "react";
import { useGlobalState } from "../contexts/GlobalStateContext";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import SimpleConfirmation from "./SimpleConfirmation";

const ShareModal = ({ uuid, onClose }) => {
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [copyStatus, setCopyStatus] = useState("Copy Link");
  const [embedStatus, setEmbedStatus] = useState('Copy Embed');
  const [sentStatus, setSentStatus] = useState({});
  const modalRef = useRef(null);
  const inputRef = useRef(null);
  const [receiverId, setReceiverId] = useState(false);
  const [blockStatus, setBlockStatus] = useState(null);
  const {
    globalState,
    setShowLogins,
    setConversations,
    logOut,
    resetGlobalState,
  } = useGlobalState();

  useEffect(() => {
    fetchContacts();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [globalState.isAuthenticated]);

  useEffect(() => {
    if (!globalState.user) return;
    if (query.trim().length > 0) {
      fetchSearchedUsers(query);
    } else {
      fetchContacts();
    }
  }, [query]);

  const handleUnblock = async () => {
    try {
      const response = await fetch(`/php/unblock_messages.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocked_id: receiverId }),
      });
      const data = await response.json();
      if (!data.status === "success") {
        alert(`Failed to unblock messages. Please try again.`);
      }
    } catch (err) {
      console.error(`Error unblocking messages:`, err);
    }
  };

  const fetchContacts = async () => {
    if (!globalState.user) return;
    try {
      const res = await fetch("/php/get_contacts.php");
      const result = await res.json();
      if (result.status === "success") {
        setUsers(result.unique_users || []);
      }
    } catch (err) {
      console.error("Error fetching contacts:", err);
    }
  };

  const fetchSearchedUsers = async (searchTerm) => {
    try {
      const res = await fetch(
        `/php/search_people.php?query=${encodeURIComponent(searchTerm)}`
      );
      const result = await res.json();
      if (result.status === "success") {
        setUsers(result.users || []);
      }
    } catch (err) {
      console.error("Search error:", err);
    }
  };

  const handleCopy = async () => {
    const link = `https://bhutani.cloud/post?id=${uuid}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("Link Copied");
      setTimeout(() => setCopyStatus("Copy Link"), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleCopyEmbed = () => {
    const embedCode = `<div style="position: relative; width: 100%; max-width: 999px; padding-bottom: 57.5%; height: 0; overflow: hidden;">
  <iframe
    src="https://em.bhutani.cloud/${uuid}"
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;"
    scrolling="no"
    allowtransparency="true"
  ></iframe>
</div>`;
    navigator.clipboard.writeText(embedCode).then(() => {
      setEmbedStatus("Embed Copied");
      setTimeout(() => setEmbedStatus("Copy Embed"), 2000);
    });
  };

  const buttonStyle = {
    width: "48%",
    padding: "10px 20px",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "bold",
    margin: "5px 1% 0",
    transition: "background-color 0.3s",
  };

  const handleSend = async (receiverId, receiverName) => {
    if (!globalState.user) {
      setShowLogins();
      return;
    }

    setSentStatus((prev) => ({ ...prev, [receiverId]: "sending" }));

    const data = { uuid, receiver: receiverId };
    try {
      const response = await fetch("/php/send_post.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (result.status === "success") {
        const currentUserId = globalState.user.user_id;
        const conversationId =
          currentUserId < receiverId
            ? `${currentUserId}-${receiverId}`
            : `${receiverId}-${currentUserId}`;

        setConversations((prevConversations) => {
          const filtered = prevConversations.filter(
            (c) => c.conversation_id !== conversationId
          );
          return [
            {
              conversation_id: conversationId,
              user_id: receiverId,
              fullName: receiverName,
              last_message: "You shared a post",
              has_unread: false,
            },
            ...filtered,
          ];
        });

        setSentStatus((prev) => ({ ...prev, [receiverId]: "sent" }));

        setTimeout(() => {
          setSentStatus((prev) => {
            const updated = { ...prev };
            delete updated[receiverId];
            return updated;
          });
        }, 3000);
      } else if (result.status === "blocked") {
        if (result.blocked_by_me) {
          setBlockStatus("blocked_by_me");
          setReceiverId(receiverId);
        } else {
          setBlockStatus("user_blocked");
          setReceiverId(receiverId);
        }
        setSentStatus((prev) => ({ ...prev, [receiverId]: "error" }));
      } else if (result.status === "error") {
        logOut();
        resetGlobalState();
      } else {
        alert("Failed to send message due to restrictions between users.");
        setSentStatus((prev) => ({ ...prev, [receiverId]: "error" }));
      }
    } catch (err) {
      console.error("Send post error:", err);
      setSentStatus((prev) => ({ ...prev, [receiverId]: "error" }));
    }
  };

  return (
    <>
      {receiverId && (
        <SimpleConfirmation
          blockStatus={blockStatus}
          onClose={() => setReceiverId(null)}
          onUnblock={handleUnblock}
        />
      )}

      {ReactDOM.createPortal(
        <div
          className="share-modal modal"
          style={{ display: "block" }}
          ref={modalRef}
        >
          <div className="share-content">
            <div id="messages-header">
              <span className="messages-title">Share</span>
              <span className="close" onClick={onClose}>
                &times;
              </span>
            </div>

            <div id="search-friends">
              <div style={{ display: "flex", justifyContent: "space-between", margin: "3px 4px 0px 7px" }}>
                <button
                  onClick={handleCopy}
                  style={{
                    ...buttonStyle,
                    backgroundColor:
                      copyStatus === "Link Copied" ? "#3b8b3b" : "#3DDC84",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#45a049")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor =
                      copyStatus === "Link Copied" ? "#3b8b3b" : "#3DDC84")
                  }
                >
                  {copyStatus}
                </button>

                <button
                  onClick={handleCopyEmbed}
                  style={{
                    ...buttonStyle,
                    backgroundColor:
                      embedStatus === "Embed Copied" ? "#3b8b3b" : "#4CAF50",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#45a049")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor =
                      embedStatus === "Embed Copied" ? "#3b8b3b" : "#4CAF50")
                  }
                >
                  {embedStatus}
                </button>
              </div>

              <input
                className="fullTextbox"
                ref={inputRef}
                type="text"
                id="search-people"
                placeholder="Search for people"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>

            <div
              id="UM-section"
              style={{
                paddingTop: 0,
                minHeight: "200px",
              }}
            >
              {globalState.user ? (
                users.length > 0 ? (
                  users.map((user) => {
                    const userId = parseInt(user.user_id, 10);
                    const status = sentStatus[userId];
                    const isSendingOrSent =
                      status === "sending" || status === "sent";

                    let buttonLabel = "Send";
                    if (status === "sent") buttonLabel = "Sent ✅";
                    else if (status === "sending") buttonLabel = "Sending...";
                    else if (status === "error") buttonLabel = "Try Again";

                    return (
                      <div
                        className="like-item"
                        key={userId}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <Link
                          to={`/@${encodeURIComponent(user.username)}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                          }}
                        >
                          <img
                            src={`profile_pics_icons/${userId}.jpg`}
                            alt={`${user.full_name}'s profile`}
                            style={{
                              width: "42px",
                              height: "42px",
                              borderRadius: "50%",
                            }}
                          />
                          <span style={{ marginLeft: "10px" }}>
                            {user.full_name}
                          </span>
                        </Link>

                        <button
                          id={`send-btn-${userId}`}
                          onClick={() =>
                            handleSend(user.user_id, user.full_name)
                          }
                          disabled={isSendingOrSent}
                          style={{
                            marginLeft: "auto",
                            padding: "5px 12px",
                            fontSize: "13px",
                            cursor: isSendingOrSent ? "not-allowed" : "pointer",
                            backgroundColor:
                              status === "sent" ? "#3b8b3b" : "#3DDC84",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          {buttonLabel}
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "40px 20px",
                      color: "#888",
                    }}
                  >
                    No contacts found.
                  </div>
                )
              ) : (
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <button
                    onClick={setShowLogins}
                    style={{
                      padding: "12px 20px",
                      backgroundColor: "blue",
                      color: "white",
                      border: "none",
                      borderRadius: "5px",
                      fontSize: "14px",
                      fontWeight: "bold",
                      cursor: "pointer",
                    }}
                  >
                    Log In to See Your Contacts
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ShareModal;
